import { Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { colorTokens, typographyTokens } from '../app/designTokens';
import { EmptyState } from './EmptyState';
import { SectionCard } from './SectionCard';
import { StatusBadge } from './StatusBadge';
import { Button } from './ui/Button';

const { semantic } = colorTokens;

function formatDate(value, emptyValue) {
  if (!value) {
    return emptyValue;
  }

  return new Date(value).toLocaleString();
}

function severityTone(severity) {
  if (severity === 'success') return 'Success';
  if (severity === 'warning') return 'Warning';
  if (severity === 'error' || severity === 'critical') return 'Danger';
  if (severity === 'info') return 'Info';
  return 'Neutral';
}

export function NotificationsPanel({
  notifications,
  onMarkRead,
  onMarkUnread,
  onDismiss,
}) {
  const { t } = useTranslation(['notifications', 'common']);
  const emptyValue = t('common:empty_value');

  if (!notifications.length) {
    return <EmptyState message={t('notifications:bell.empty')} />;
  }

  return (
    <Stack spacing={1.5}>
      {notifications.map((notification) => (
        <SectionCard
          key={notification.id}
          variant={notification.is_read ? 'Default' : 'Selected'}
          sx={{ mb: 0 }}
        >
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
            <Typography
              sx={{
                flexGrow: 1,
                minWidth: 0,
                fontFamily: typographyTokens.fontFamily,
                fontWeight: notification.is_read
                  ? typographyTokens.fontWeightMedium
                  : typographyTokens.fontWeightSemibold,
                fontSize: 14,
                lineHeight: '20px',
                color: semantic.text.primary,
              }}
            >
              {notification.title}
            </Typography>
            {!notification.is_read ? (
              <StatusBadge
                status="unread"
                category="generic"
                apiLabel={t('notifications:inbox.unread')}
                size="Sm"
                tone="Info"
              />
            ) : null}
            {notification.category ? (
              <StatusBadge
                status={notification.category}
                category="generic"
                apiLabel={t(`notifications:preferences.categories.${notification.category}`, {
                  defaultValue: notification.category,
                })}
                size="Sm"
                tone="Neutral"
              />
            ) : null}
            {notification.severity ? (
              <StatusBadge
                status={notification.severity}
                category="generic"
                apiLabel={t(`notifications:inbox.severities.${notification.severity}`, {
                  defaultValue: notification.severity,
                })}
                size="Sm"
                tone={severityTone(notification.severity)}
              />
            ) : null}
          </Stack>

          {notification.body ? (
            <Typography
              sx={{
                mb: 1,
                fontSize: 13,
                lineHeight: '18px',
                color: semantic.text.secondary,
                whiteSpace: 'pre-wrap',
              }}
            >
              {notification.body}
            </Typography>
          ) : null}

          <Typography
            sx={{
              display: 'block',
              mb: 1.5,
              fontSize: 12,
              color: semantic.text.muted,
            }}
          >
            {formatDate(notification.created_at, emptyValue)}
          </Typography>

          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {notification.is_read ? (
              <Button
                variant="Ghost"
                onClick={() => onMarkUnread(notification)}
                sx={{ minHeight: 32, px: 1.25 }}
              >
                {t('notifications:inbox.mark_unread')}
              </Button>
            ) : (
              <Button
                variant="Secondary"
                onClick={() => onMarkRead(notification)}
                sx={{ minHeight: 32, px: 1.25 }}
              >
                {t('notifications:inbox.mark_read')}
              </Button>
            )}
            <Button
              variant="Ghost"
              onClick={() => onDismiss(notification)}
              sx={{ minHeight: 32, px: 1.25 }}
            >
              {t('notifications:inbox.dismiss')}
            </Button>
          </Stack>
        </SectionCard>
      ))}
    </Stack>
  );
}
